// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import Axios from 'axios';

import HotspotItem, { HotspotItemDTO } from '@shared/models/dashboard/hotspot-item';

import BaseService from '@core/services/base';
import { ListRequestParams, ListRequestResponse, PageInfo } from '@shared/types/services';
import { REQUEST_PARAMS } from '@shared/constants/services';
import {
//  OrderStanding,
  HOTSPOT_FIELDS,
//  HOTSPOT_DETAILS_FIELDS,
//  ORDER_STANDING_LABELS,
} from '@shared/constants/dashboard';
import { hotspotTestDto1, hotspotTestDto2 } from '@shared/constants/hotspots';
@injectable()
export default class HotspotsService extends BaseService<HotspotItemDTO, HotspotItem> {
  static diToken = Symbol('hotspots-service');
  static testHotspots = false; //WXYZ
  constructor() {
    // domainArea is the URL prefix (endpoint).  This object belongs to a section on the Dashboard page.
    super({ domainArea: 'dashboard/hotspots', Model: HotspotItem });
  }

  // Setup a query structure to retrieve data.
  // Note: pagination is not used.  Check if it can be removed.
  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        hotspots(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          ${REQUEST_PARAMS.filtering.fields.dateRange}: "${filtering?.dateRange || []}",
          sort: { field: ${sorting?.field}, type: ${sorting?.type}},
        ) {
          items {
            ${HOTSPOT_FIELDS.orderId},
            ${HOTSPOT_FIELDS.orderNumber},
            ${HOTSPOT_FIELDS.orderName},
            ${HOTSPOT_FIELDS.customerName},
            ${HOTSPOT_FIELDS.customerId},
            ${HOTSPOT_FIELDS.dueDate},            
            ${HOTSPOT_FIELDS.notes},
            ${HOTSPOT_FIELDS.statusSummaryString},
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  data2Int(val: string | number): number {
    if (typeof val === 'number') {
      return val;
    }
    const tmp = parseInt(val, 10);
    if(isNaN(tmp)) {
      return 0;
    }
    
      return parseInt(val, 10);
    
  }

  // Get a list of notifications from the portal-api with items seleccted based on a uery structure (item and page info).
  async getList(params: ListRequestParams) {

    if (HotspotsService.testHotspots === false) {
      const { data } = await Axios.get('/dashboard/hotspots', {
        params: { query: this.getListQuery(params) },
      });
      // const { data } = await Axios.get<{ hotspots: ListRequestResponse<HotspotItem> }>('/dashboard/hotspots', {
      //  params: { query: this.getListQuery(params) },
      // });

      const { hotspots: { items, pageInfo }, } = data;

      const dtoItems: HotspotItemDTO[] = [];
      for(const item of items) {
        //  0      1  2  3  4  5         6 7 8 9 10        1112131415        1617181920        21222324
        // "Assets 10 10 10 10 Scheduled 1 2 3 4 Collected 1 2 3 4 Processed 1 2 3 4 Delivered 1 2 3 4"
        const terms = item.statusSummaryString.split(' ').map((term) => this.data2Int(term));
        dtoItems.push({
          orderId: item.id,
          order: item.orderName,
          customerId: item.customerId,
          customer: item.customerName,
          assets: {
            standing: (terms[1] + terms[2] + terms[3] + terms[4]).toString(),
            normalCount: terms[1],
            warningCount: terms[2],
            criticalCount: terms[3],
            overdueCount: terms[4],
          },
          scheduled: {
            standing: (terms[6] + terms[7] + terms[8] + terms[9]).toString(),
            normalCount: terms[6],
            warningCount: terms[7],
            criticalCount: terms[8],
            overdueCount: terms[9],
          },
          collected: {
            standing: (terms[11] + terms[12] + terms[13] + terms[14]).toString(),
            normalCount: terms[11],
            warningCount: terms[12],
            criticalCount: terms[13],
            overdueCount: terms[14],
          },
          processed: {
            standing: (terms[16] + terms[17] + terms[18] + terms[19]).toString(),
            normalCount: terms[16],
            warningCount: terms[17],
            criticalCount: terms[18],
            overdueCount: terms[19],
          },
          delivered: {
            standing: (terms[21] + terms[22] + terms[23] + terms[24]).toString(),
            normalCount: terms[21],
            warningCount: terms[22],
            criticalCount: terms[23],
            overdueCount: terms[24],
          },
          dueDate: item.dueDate,              
        });
      }

      return {
        pageInfo,
        items: dtoItems.map((dto) => new HotspotItem(dto)),
      };
    }
    
    {
      const pageInfoTmp: PageInfo = {
        totalCount: 1,
        hasPreviousPage: false,
        hasNextPage: false,
      };
  
      const data1 = {
        hotspots: {
          items: [hotspotTestDto1, hotspotTestDto2],
          pageInfo: pageInfoTmp,
        },
      };

      const {
        hotspots: { items, pageInfo },
      } = data1;

      return {
        pageInfo,
        items: items.map((dto) => new HotspotItem(dto)),
      };
    }
  }

  // cut-paste, probably not needed.
  getInfoFromListResponse(data: { [key: string]: ListRequestResponse<HotspotItem> }) {
    const {
      notificationItems: { items, pageInfo },
    } = data;

    return { items, pageInfo };
  }
}
